import { inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isEqual, throttle } from 'lodash';
import { debounce, delay, firstValueFrom, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ShapeState2D } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { DEFAULT_ZOOM_PANEL_SCALE } from '../../../cross-team-shared/constants';
import { midPoint } from '../../../shared/misc-utilities/point-utils';
import { IN_PROGRESS_INDEX } from '../../../team-apis/core/reserved-shape-indexes';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { ShapeBoundingPointsService } from '../utils/shape-bounding-points.service';
import { ViewBoxPaddingService } from '../utils/view-box-padding.service';
import { ViewBoxService } from '../utils/view-box.service';

@Injectable()
export class InspectService extends DirectiveSuperclass {
  private store = inject(AbstractSensorContentStore2D);
  private viewBoxService = inject(ViewBoxService);
  private shapeBoundingPointsService = inject(ShapeBoundingPointsService);
  private viewBoxPaddingService = inject(ViewBoxPaddingService);
  private pauseInspectPromise: Promise<void> = firstValueFrom(
    this.store('status').$.pipe(
      filter((status) => status === 'ready'),
      // Need to delay on macro queue for the initial resize to be finished
      delay(0),
      map(() => void 0),
    ),
  );
  private inspectionCompleted$ = new Subject<void>();

  constructor() {
    super();

    this.subscribeTo(
      this.inspectionCompleted$.pipe(
        debounce(() =>
          this.store('isZoomPanelOpen').$.pipe(filter((isOpen) => isOpen)),
        ),
      ),
      () => {
        this.store('layerSettings')('zoomPanelScale').set(
          this.store.state().scale * DEFAULT_ZOOM_PANEL_SCALE,
        );
      },
    );
  }

  pauseUntil(promise: Promise<void>) {
    this.pauseInspectPromise = promise;
  }

  async inspectFocusedShape() {
    const focusedShapeStore = this.store.getFocusedShapeStore();

    if (
      this.store.getFocusedShapeIndex() === IN_PROGRESS_INDEX ||
      !focusedShapeStore
    ) {
      // Don't inspect while creating a new shape
      return;
    }

    try {
      await this.pauseInspectPromise;
    } catch {
      // once the promise is fulfilled or rejected we continue to inspect
    }
    await this.inspectShape(focusedShapeStore);
  }

  // inspecting twice in the same tick can lead to viewbox being "moved" twice,
  // something to do with async I think
  // eslint-disable-next-line @typescript-eslint/member-ordering -- for debounced methods, it's cleaner to leave with methods
  inspectShape = throttle(
    async (shapeStore: Store<ShapeState2D>) => {
      const boundingPoints =
        this.shapeBoundingPointsService.getBoundingPoints(shapeStore);

      if (!boundingPoints) {
        return;
      }

      const isPointShape = isEqual(boundingPoints[0], boundingPoints[1]);
      if (!isPointShape) {
        const paddedBoundingPoints =
          this.viewBoxPaddingService.addPaddingToBoundingPoints(boundingPoints);
        await this.viewBoxService.zoomToBox(paddedBoundingPoints, 'inspect');
        this.viewBoxPaddingService.updatePadding();
      } else {
        await this.panToCenterOfBoundingPoints(boundingPoints);
      }

      this.inspectionCompleted$.next();
    },
    0,
    { leading: false, trailing: true },
  );

  panToCenterOfBoundingPoints(boundingPoints: [Point2D, Point2D]) {
    return this.viewBoxService.centerToPoint(
      midPoint(boundingPoints),
      'inspect',
    );
  }
}
